// ============================================================================
// FSA NOTE MODAL — DISCLOSURE VALIDATION ENGINE
// ============================================================================
// JSON-declared validation rules on additional disclosures. The seed
// authors cross-row / cross-column consistency checks directly in the
// disclosure spec; the engine evaluates them against the current grid
// state and surfaces warnings inline.
//
// All rules are warn-only per the FSA-2.2 decision — the auditor can
// always save the note. The approver sees the warnings list at
// submission time (wired in FSA-3 approval surface).
//
// JSON shape on each disclosure spec:
//   "validations": [
//     {
//       "code": "py_closing_eq_cy_opening_amount",
//       "rule": "match",
//       "message": "Previous-year closing must equal current-year opening (amount).",
//       "left":  { "row_key": "closing", "column": "amount_py" },
//       "right": { "row_key": "opening", "column": "amount_cy" },
//       "tolerance": 0.01                  // optional; default 0.01
//     }
//   ]

export type ValidationSeverity = 'warning' | 'error';

export type ValidationRuleType = 'match';

export interface ValidationCellRef {
  /** Identifier for the row — matches the fixed_row's `row_key`. */
  row_key: string;
  /** Column code in the column_spec. */
  column: string;
}

export interface ValidationRule {
  code: string;
  rule: ValidationRuleType;
  message: string;
  left: ValidationCellRef;
  right: ValidationCellRef;
  tolerance?: number;
  severity?: ValidationSeverity;
}

export interface ValidationResult {
  code: string;
  message: string;
  severity: ValidationSeverity;
  leftValue: number;
  rightValue: number;
  delta: number;
  leftCell: ValidationCellRef;
  rightCell: ValidationCellRef;
}

type Row = Record<string, string | number | null>;

const DEFAULT_TOLERANCE = 0.01;

/**
 * Evaluate every rule against the current grid state. Returns one
 * ValidationResult per FAILING rule. Passing rules are omitted so the UI
 * can render the result array directly.
 *
 * @param rules           — validation rules from the disclosure spec.
 * @param rows            — current grid rows (one row per fixed_row, in spec order).
 * @param rowKeysInOrder  — row_key for each row index (same order as rows).
 */
export function evaluateValidations(
  rules: ValidationRule[],
  rows: Row[],
  rowKeysInOrder: string[],
): ValidationResult[] {
  if (!rules || rules.length === 0) return [];
  const out: ValidationResult[] = [];

  const rowIndexByKey = new Map<string, number>();
  rowKeysInOrder.forEach((key, i) => {
    if (key) rowIndexByKey.set(key, i);
  });

  for (const r of rules) {
    if (r.rule !== 'match') continue; // future rule types layer in here.

    const lIdx = rowIndexByKey.get(r.left.row_key);
    const rIdx = rowIndexByKey.get(r.right.row_key);
    if (lIdx == null || rIdx == null) continue;

    const lRaw = rows[lIdx]?.[r.left.column];
    const rRaw = rows[rIdx]?.[r.right.column];
    const lVal = toNumber(lRaw);
    const rVal = toNumber(rRaw);

    const tolerance = r.tolerance ?? DEFAULT_TOLERANCE;
    const delta = lVal - rVal;
    if (Math.abs(delta) <= tolerance) continue;

    out.push({
      code: r.code,
      message: r.message,
      severity: r.severity ?? 'warning',
      leftValue: lVal,
      rightValue: rVal,
      delta,
      leftCell: r.left,
      rightCell: r.right,
    });
  }

  return out;
}

function toNumber(v: string | number | null | undefined): number {
  if (typeof v === 'number') return v;
  if (v == null || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}
